/**
 * DIEUKYDIEU.TV - AUDIO PLAYER SWIPE & TOUCH GESTURES
 */

import { closeFullPlayer, closePlayer } from './player-ui.js';

export function initPlayerSwipeGestures() {
    initMiniPlayerSwipeDismiss();

    const fp = document.getElementById('full-player');
    if (!fp) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;
    let startTime = 0;

    // iOS-style: swipe down anywhere on the sheet to dismiss (drag handle still
    // works via bubbling). Excludes scrollable panes and controls so lyrics /
    // queue scrolling, scrubbing and taps behave normally.
    const touchTarget = fp;
    const SWIPE_EXCLUDE_SEL = '.fp-lyrics-wrap, .fp-queue-list, input, button, a, select, textarea, [role="slider"]';

    touchTarget.addEventListener('touchstart', (e) => {
        if (window.innerWidth >= 768) return;
        if (!fp.classList.contains('show')) return;
        if (e.target.closest(SWIPE_EXCLUDE_SEL)) return;

        const touch = e.touches[0];
        startY = touch.clientY;
        currentY = startY;
        startTime = Date.now();
        isDragging = true;
        fp.style.transition = 'none';
    }, { passive: true });

    touchTarget.addEventListener('touchmove', (e) => {
        if (!isDragging || window.innerWidth >= 768) return;
        const touch = e.touches[0];
        const deltaY = touch.clientY - startY;

        if (deltaY > 0) {
            currentY = touch.clientY;
            fp.style.transform = `translateY(${deltaY}px)`;

            const backdrop = document.getElementById('full-player-backdrop');
            if (backdrop) {
                const opacity = Math.max(0, 1 - (deltaY / 300));
                backdrop.style.opacity = opacity.toString();
            }
        }
    }, { passive: true });

    const handleTouchEnd = () => {
        if (!isDragging || window.innerWidth >= 768) return;
        isDragging = false;
        fp.style.transition = '';

        const backdrop = document.getElementById('full-player-backdrop');
        if (backdrop) backdrop.style.opacity = '';

        const deltaY = currentY - startY;
        const timeElapsed = Date.now() - startTime;
        const velocity = deltaY / Math.max(1, timeElapsed);

        if (deltaY > 80 || (velocity > 0.4 && deltaY > 30)) {
            closeFullPlayer();
            setTimeout(() => {
                fp.style.transform = '';
            }, 350);
        } else {
            fp.style.transform = '';
        }
    };

    touchTarget.addEventListener('touchend', handleTouchEnd);
    touchTarget.addEventListener('touchcancel', handleTouchEnd);
}

export function initMiniPlayerSwipeDismiss() {
    const miniPlayer = document.getElementById('mini-player');
    if (!miniPlayer) return;

    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let isSwiping = false;
    let isPointerDown = false;
    let startTime = 0;
    let wasSwiped = false;

    // Prevent openFullPlayer() when a swipe dismiss gesture occurred
    miniPlayer.addEventListener('click', (e) => {
        if (wasSwiped) {
            e.stopPropagation();
            e.stopImmediatePropagation();
            wasSwiped = false;
        }
    }, true);

    const handlePointerDown = (e) => {
        // Only accept primary button (left click) or touch/pen
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        if (e.target.closest('.mp-btn')) return;
        if (!miniPlayer.classList.contains('active')) return;

        startX = e.clientX;
        startY = e.clientY;
        currentX = startX;
        startTime = Date.now();
        isPointerDown = true;
        isSwiping = false;
        wasSwiped = false;

        try {
            miniPlayer.setPointerCapture(e.pointerId);
        } catch (_) {}
    };

    const handlePointerMove = (e) => {
        if (!isPointerDown) return;
        if (!miniPlayer.classList.contains('active')) return;

        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;

        // Detect horizontal swipe intent (prioritize horizontal over vertical)
        if (!isSwiping && Math.abs(deltaX) > 6 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
            isSwiping = true;
            miniPlayer.style.transition = 'none';
            miniPlayer.classList.add('is-swiping');
        }

        if (isSwiping) {
            currentX = e.clientX;
            miniPlayer.style.transform = `translateX(${deltaX}px)`;
            const opacity = Math.max(0.1, 1 - (Math.abs(deltaX) / 240));
            miniPlayer.style.opacity = opacity.toString();
        }
    };

    const handlePointerEnd = (e) => {
        if (!isPointerDown) return;
        isPointerDown = false;

        try {
            if (miniPlayer.hasPointerCapture(e.pointerId)) {
                miniPlayer.releasePointerCapture(e.pointerId);
            }
        } catch (_) {}

        miniPlayer.classList.remove('is-swiping');

        if (!isSwiping) {
            miniPlayer.style.transition = '';
            return;
        }

        isSwiping = false;
        const deltaX = currentX - startX;
        const timeElapsed = Date.now() - startTime;
        const velocity = Math.abs(deltaX) / Math.max(1, timeElapsed);

        // Threshold: dragged > 75px or flicked with velocity > 0.35 & deltaX > 30px
        if (Math.abs(deltaX) > 75 || (velocity > 0.35 && Math.abs(deltaX) > 30)) {
            wasSwiped = true;
            const flyOutX = deltaX > 0 ? (window.innerWidth || 450) : -(window.innerWidth || 450);
            miniPlayer.style.transition = 'transform 0.28s cubic-bezier(0.2, 0.9, 0.3, 1), opacity 0.25s ease';
            miniPlayer.style.transform = `translateX(${flyOutX}px)`;
            miniPlayer.style.opacity = '0';

            setTimeout(() => {
                closePlayer();
                miniPlayer.style.transition = '';
                miniPlayer.style.transform = '';
                miniPlayer.style.opacity = '';
                setTimeout(() => {
                    wasSwiped = false;
                }, 100);
            }, 260);
        } else {
            if (Math.abs(deltaX) > 10) {
                wasSwiped = true;
                setTimeout(() => {
                    wasSwiped = false;
                }, 200);
            }
            miniPlayer.style.transition = 'transform 0.35s var(--md-motion-expressive-spring), opacity 0.25s ease';
            miniPlayer.style.transform = '';
            miniPlayer.style.opacity = '';
        }
    };

    miniPlayer.addEventListener('pointerdown', handlePointerDown);
    miniPlayer.addEventListener('pointermove', handlePointerMove);
    miniPlayer.addEventListener('pointerup', handlePointerEnd);
    miniPlayer.addEventListener('pointercancel', handlePointerEnd);
}
